import React from 'react'

import { TDashboardGroup } from 'types/GlobalDashboardGroup'
import { TGroup } from 'types/GlobalGroup'
import { getResourceVariant, TKeyMetric } from 'types/GlobalKeyMetric'
import { TReport } from 'types/GlobalReport'

import { useTranslation } from 'language/translator'
import { useAccount, useAccounts } from 'redux/hooks/Accounts'
import { useAvailableDashboardGroups } from 'redux/hooks/DashboardGroups'
import { useGroups } from 'redux/hooks/Groups'
import { useReports } from 'redux/hooks/Reports'

import FormSection from 'components_new/molecules/FormSection'
import SelectSearch from 'components_new/molecules/SelectSearch'

import { formatUsername } from 'utils/functions'

interface Props {
  editKeyMetric: TKeyMetric | null
  onKeyMetricChange: (update: Partial<TKeyMetric>) => void
}

const MetricEditSidebar = (props: Props) => {
  const { editKeyMetric, onKeyMetricChange } = props

  const { t } = useTranslation()
  const { allGroups } = useGroups()
  const { accounts } = useAccounts()
  const dashboardGroups = useAvailableDashboardGroups()
  const allReports = useReports()
  const responsible = useAccount(editKeyMetric?.responsible_account_id ?? null)

  return (
    <>
      <FormSection size="small" title={t('global', 'owner')}>
        <SelectSearch
          multiple={false}
          onChange={(value) =>
            onKeyMetricChange({ responsible_account_id: value?.id ?? null })
          }
          options={accounts.map((acc) => ({
            avatar: 'user',
            body: acc.email,
            id: acc.id,
            title: formatUsername(acc)
          }))}
          selected={
            responsible
              ? {
                  avatar: 'user',
                  body: responsible.email,
                  id: responsible.id,
                  title: formatUsername(responsible)
                }
              : null
          }
        />
      </FormSection>

      <FormSection size="small" title={t('global', 'groups')}>
        <SelectSearch
          multiple
          onChange={(value) => {
            const groups: Omit<TGroup, 'key_metrics'>[] = value
              .map((v) => Object.values(allGroups).find((g) => g.id === v.id))
              .filter((g): g is TGroup => g !== undefined)

            onKeyMetricChange({ groups })
          }}
          options={Object.values(allGroups).map((group) => ({
            avatar: 'group',
            color: group.profile_color,
            id: group.id,
            title: group.name
          }))}
          selected={
            editKeyMetric?.groups.map((group) => ({
              avatar: 'group' as const,
              color: group.profile_color,
              id: group.id,
              title: group.name
            })) ?? []
          }
        />
      </FormSection>

      <FormSection size="small" title={t('global', 'linked_resources')}>
        <SelectSearch
          multiple
          onChange={(value) => {
            const resources: (TDashboardGroup | TReport)[] = value
              .map((v) => {
                const dg = dashboardGroups.find((d) => d.id === v.id)

                if (dg) return dg

                return allReports.find((r) => r.id === v.id)
              })
              .filter(Boolean) as (TDashboardGroup | TReport)[]

            onKeyMetricChange({ resources })
          }}
          options={[
            ...dashboardGroups.map((dg) => ({
              avatar: 'dashboard' as const,
              body: t('global', 'dashboard'),
              id: dg.id,
              title: dg.title
            })),
            ...allReports.map((r) => ({
              avatar: 'report' as const,
              body: t('global', 'report'),
              id: r.id,
              title: r.title
            }))
          ]}
          selected={
            editKeyMetric?.resources.map((r) => ({
              avatar: getResourceVariant(r),
              id: r.id,
              title: r.title
            })) ?? []
          }
        />
      </FormSection>
    </>
  )
}

export default MetricEditSidebar
